import { useAsyncData } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import {
  type BusinessDetails,
  type KybResponse,
  type Stakeholder,
  StakeholderRole,
} from '~/api/kyb/types'
import type { StakeholderFormValues } from '~/scopes/kyb/pages/components/steps/components/StakeholderForm/StakeholderForm.types'

import { transformStakeholderFormToApi } from '../utils/kyb.utils'

const KYB_DATA_KEY = 'kyb-data'

export function useKybData() {
  const $httpFetch = useHttpFetch()

  const {
    data: kybData,
    status,
    refresh: refreshKybData,
    execute: fetchKybData,
  } = useAsyncData<KybResponse>(
    KYB_DATA_KEY,
    () => $httpFetch('/api/payout-experience/v1/kyb'),
    {
      immediate: false,
    },
  )

  const isLoading = computed(() => status.value === 'pending')

  const steps = computed(() => kybData.value?.steps ?? [])
  const errors = computed(() => kybData.value?.errors ?? [])

  const businessDetails = computed(() => kybData.value?.data?.business)
  const stakeholders = computed(() => kybData.value?.data?.stakeholders ?? [])
  const letterOfAuthorization = computed(
    () => kybData.value?.data?.letterOfAuthorization,
  )

  const accountRequester = computed(() =>
    stakeholders.value.find((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.ACCOUNT_REQUESTER),
    ),
  )

  const legalRepresentative = computed(() =>
    stakeholders.value.find((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.LEGAL_REPRESENTATIVE),
    ),
  )

  const primaryBeneficialOwner = computed(() =>
    stakeholders.value.find((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.PRIMARY_BENEFICIAL_OWNER),
    ),
  )

  const secondaryBeneficialOwners = computed(() =>
    stakeholders.value.filter((stakeholder) =>
      stakeholder.roles.includes(StakeholderRole.SECONDARY_BENEFICIAL_OWNER),
    ),
  )

  async function saveBusinessDetails(business: BusinessDetails) {
    if (businessDetails.value) {
      await updateBusinessDetails(business)
    } else {
      await createBusinessDetails(business)
    }
  }

  async function updateBusinessDetails(business: BusinessDetails) {
    await $httpFetch('/api/payout-experience/v1/kyb/business-details', {
      method: 'PUT',
      body: business,
    })
  }

  async function createBusinessDetails(business: BusinessDetails) {
    await $httpFetch('/api/payout-experience/v1/kyb/business-details', {
      method: 'POST',
      body: business,
    })
  }
  async function updateStakeholder(stakeholder: Stakeholder) {
    if (!stakeholder.id) {
      throw new Error('Stakeholder not found')
    }

    await $httpFetch('/api/payout-experience/v1/kyb/stakeholders/:id', {
      method: 'PUT',
      pathParams: { id: stakeholder.id },
      body: stakeholder,
    })
  }

  async function createStakeholder(
    stakeholder: Omit<Stakeholder, 'id'> & { id?: string | null },
  ) {
    await $httpFetch('/api/payout-experience/v1/kyb/stakeholders', {
      method: 'POST',
      body: stakeholder,
    })
  }

  async function saveStakeholder(
    formValues: StakeholderFormValues,
    roles: StakeholderRole[],
  ) {
    const stakeholderData = transformStakeholderFormToApi(
      formValues,
      roles,
      formValues.id,
    )

    if (stakeholderData.id) {
      await updateStakeholder(stakeholderData)
    } else {
      await createStakeholder(stakeholderData)
    }
  }

  async function addRolesToStakeholder(
    stakeholder: Stakeholder,
    roles: StakeholderRole[],
  ) {
    stakeholder.roles.push(...roles)

    await updateStakeholder(stakeholder)
  }

  async function deleteStakeholder(stakeholderId: string) {
    await $httpFetch('/api/payout-experience/v1/kyb/stakeholders/:id', {
      method: 'DELETE',
      pathParams: { id: stakeholderId },
    })
  }

  const isLegalRepresentativeSameAsAccountRequester = computed(() => {
    return legalRepresentative.value?.id === accountRequester.value?.id
  })

  const isBeneficialOwnerSameAsAccountRequester = computed(() => {
    return primaryBeneficialOwner.value?.id === accountRequester.value?.id
  })

  const isBeneficialOwnerSameAsLegalRepresentative = computed(() => {
    return primaryBeneficialOwner.value?.id === legalRepresentative.value?.id
  })

  async function submitKyb() {
    await $httpFetch('/api/payout-experience/v1/kyb/submit', {
      method: 'POST',
    })
  }

  return {
    steps,
    kybData,
    status,
    errors,
    businessDetails,
    stakeholders,
    letterOfAuthorization,
    accountRequester,
    legalRepresentative,
    primaryBeneficialOwner,
    secondaryBeneficialOwners,
    isLoading,
    fetchKybData,
    refreshKybData,
    saveBusinessDetails,
    updateStakeholder,
    createStakeholder,
    saveStakeholder,
    addRolesToStakeholder,
    deleteStakeholder,
    submitKyb,
    isLegalRepresentativeSameAsAccountRequester,
    isBeneficialOwnerSameAsAccountRequester,
    isBeneficialOwnerSameAsLegalRepresentative,
  }
}
